import { type PropsWithChildren } from "react";

import { Button as AriaButton, type PressEvent } from "react-aria-components";
import { tv } from "tailwind-variants";

export interface CardProps extends PropsWithChildren {
  className?: string;
  variant?: "statCard" | "muted";
  elevation?: "none" | "sm" | "md" | "lg";
  /** When provided, makes the card clickable with proper accessibility */
  onPress?: (e: PressEvent) => void;
}

const card = tv({
  base: "rounded-sm bg-background-primary p-3",
  variants: {
    variant: {
      statCard: "bg-background-tertiary flex flex-col justify-center w-full",
      muted: "bg-background-tertiary",
    },
    elevation: {
      none: "shadow-none",
      sm: "shadow-sm",
      md: "shadow-md",
      lg: "shadow-lg",
    },
    isClickable: {
      true: "cursor-pointer w-full text-left border border-outline-decorative transition-colors duration-200 hover:border-outline-brand",
      false: "",
    },
  },
  defaultVariants: {
    elevation: "none",
    isClickable: false,
  },
});

export function Card({ variant, elevation, className, children, onPress }: CardProps) {
  const isClickable = !!onPress;
  const cardClasses = card({ variant, elevation, isClickable, className });

  if (isClickable) {
    return (
      <AriaButton className={cardClasses} onPress={onPress}>
        {children}
      </AriaButton>
    );
  }

  return <div className={cardClasses}>{children}</div>;
}
